<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="500px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title class="justify-space-between">
                <h1 class="text-subtitle-1">
                    Wyszukiwanie
                </h1>
                <v-icon
                    v-if="hasCamera"
                    icon
                    color="primary"
                    class="hidden-md-and-up"
                    @click="showCodeReaderDialog"
                >
                    mdi-camera
                </v-icon>
            </v-card-title>

            <v-card-title>
                <v-form @submit.prevent="findOrder(taskTypeId, taskNumber)">
                    <v-container class="pa-0">
                        <v-row>
                            <v-col cols="12">
                                <v-radio-group
                                    v-model="taskTypeId"
                                    :error-messages="taskTypeErrors()"
                                    row
                                    required
                                    hide-details
                                    @change="resetTaskSearch"
                                >
                                    <v-radio
                                        v-for="(item) in taskTypeNames"
                                        :key="item.id"
                                        :value="item.id"
                                        :label="item.name"
                                        :disabled="findOrderLoading"
                                    />
                                </v-radio-group>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-if="taskTypeId === 1"
                                    v-model="taskNumber"
                                    :items="productionOrders"
                                    :search-input.sync="productionOrderSearch"
                                    :error-messages="taskNumberErrors()"
                                    :filter="customProductionOrderFilter"
                                    :loading="productionOrderSelectLoading || findOrderLoading"
                                    label="Numer zlecenia produkcyjnego"
                                    class="v-text-field--placeholder-dark"
                                    required
                                    dense
                                    item-value="jobNumber"
                                    no-data-text="Nie znaleziono"
                                    clearable
                                    filled
                                    @change="getProductionOrder"
                                >
                                    <template slot="selection" slot-scope="data">
                                        {{ data.item.jobNumber }}
                                    </template>

                                    <template slot="item" slot-scope="data">
                                        {{ data.item.jobNumber }}
                                    </template>

                                    <template slot="no-data">
                                        <v-list-item>
                                            <v-list-item-title>
                                                <template v-if="!productionOrderSearch">
                                                    Wpisz treść aby odnaleźć <strong>zlecenie</strong>.
                                                </template>
                                                <template v-else-if="productionOrderSearch.length < productionOrderSearchMinQueryLength">
                                                    Wpisz przynajmniej <strong>{{ productionOrderSearchMinQueryLength }}</strong> znaki.
                                                </template>
                                                <template v-else-if="productionOrderSelectLoading">
                                                    Trwa wyszukiwanie zleceń...
                                                </template>
                                                <template v-else>
                                                    Nie odnaleziono zleceń na zapytanie: "<strong> {{ productionOrderSearch }}".</strong>
                                                </template>
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>

                                <v-select
                                    v-if="taskTypeId === 1"
                                    v-model="taskId"
                                    :items="plates"
                                    :disabled="findOrderLoading || !isTaskFound"
                                    :loading="findPlateLoading"
                                    label="Montaż"
                                    item-value="custom_id"
                                    filled
                                    dense
                                    hide-details
                                    @change="getProductionOrder(productionOrder.jobNumber, taskId)"
                                >
                                    <template slot="selection" slot-scope="data">
                                        Montaż {{ data.item.plateNumber }}
                                        <v-chip
                                            v-if="data.item.isFullReported"
                                            color="success"
                                            text-color="success"
                                            small
                                            outlined
                                        >
                                            zaraportowany
                                        </v-chip>
                                    </template>
                                    <template slot="item" slot-scope="data">
                                        Montaż {{ data.item.plateNumber }}
                                        <v-chip
                                            v-if="data.item.isFullReported"
                                            color="success"
                                            text-color="success"
                                            small
                                            outlined
                                        >
                                            zaraportowany
                                        </v-chip>
                                    </template>
                                </v-select>

                                <v-text-field
                                    v-else
                                    v-model="taskNumber"
                                    :error-messages="taskNumberErrors()"
                                    :disabled="findOrderLoading"
                                    :loading="findOrderLoading"
                                    label="Numer zamówienia"
                                    append-outer-icon="mdi-magnify"
                                    filled
                                    required
                                    @click:append-outer="findOrder(taskTypeId, taskNumber)"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-title>

            <v-card-text v-if="isTaskFound">
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="12">
                            <simple-table :data="task">
                                <template #header="{paramName}">
                                    {{ $t('reports.objectDetails.' + paramName) }}:
                                </template>
                            </simple-table>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Dodaj do schowka"
                    :disabled="!isTaskFound || findPlateLoading"
                    :loading="actionLoading"
                    @click="onConfirm"
                >
                    Dodaj do schowka
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import SimpleTable from '@/components/common/SimpleTable'
import { required } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { isEmpty } from 'lodash'

let productionOrderSearchTimer

export default {
    name: 'TaskDialog',
    components: {
        SimpleTable
    },
    props: {
        isOpen: {
            type: Boolean
        },
        qrCode: {
            type: Object,
            default: null
        },
        hasCamera: {
            type: Boolean
        },
        actionLoading: {
            type: Boolean
        }
    },
    data() {
        return {
            taskTypeNames: [
                { id: 1, name: 'Zlecenie produkcyjne' },
                { id: 2, name: 'Zamówienie' }
            ],
            taskTypeId: 1,
            taskId: null,
            taskNumber: null,
            task: {},
            findOrderLoading: false,
            productionOrder: null,
            productionOrderSearch: null,
            productionOrderSearchMinQueryLength: 4,
            productionOrders: [],
            productionOrderSelectLoading: false,
            findPlateLoading: false,
            startTrackingLoading: false,
            isTaskFound: false,
            isProductionOrder: null,
            codeReaderSettings: {
                expectedQrTypeCodes: ['KZ', 'KZM', 'KP'],
                origin: 'dialogCamera'
            }
        }
    },
    validations() {
        return {
            taskTypeId: {
                required
            },
            taskNumber: {
                required
            }
        }
    },
    computed: {
        plates() {
            if (!this.isTaskFound) return

            return this.productionOrder.plates
        },
    },
    watch: {
        productionOrderSearch(value = null) {
            if (value === null || value.length < this.productionOrderSearchMinQueryLength) return

            this.productionOrderSelectLoading = true

            clearTimeout(productionOrderSearchTimer)

            productionOrderSearchTimer = setTimeout(() => {
                if (value) {
                    this.searchProductionOrders(value)
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    this.productionOrderSelectLoading = false
                    this.productionOrders = []
                }
            }, 500)
        },
        qrCode(qr) {
            if (this.codeReaderSettings.expectedQrTypeCodes.includes(qr.type)) {
                this.taskTypeId = qr.taskTypeId
                this.taskNumber = qr.taskNumber
                this.taskId = qr.customId
                this.findOrder(this.taskTypeId, this.taskNumber, this.taskId)
            }
        }
    },
    methods: {
        async searchProductionOrders(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie zamówień')

            return await this.$store.dispatch('shipments/manageTracking/findProductionOrder', {
                query: query,
                machineId: this.machineId
            })
                .then(response => {
                    this.productionOrders = response.data.results

                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.productionOrderSelectLoading = false
                })
        },
        customProductionOrderFilter(item, queryText) {
            const productionOrder = item.jobNumber.toLowerCase(),
                search = queryText.toLowerCase()

            return productionOrder.indexOf(search) > -1
        },
        findOrder(taskTypeId, taskNumber, customId) {
            this.$v.taskTypeId.$touch()
            this.$v.taskNumber.$touch()

            if (this.$v.taskTypeId.$invalid || this.$v.taskNumber.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę wypełnić wymagane pola.'
                })

                return
            }

            this.findOrderLoading = true

            switch (taskTypeId) {
                case 1:
                    this.searchProductionOrders(taskNumber)
                        .then(response => {
                            if (response.length > 0) {
                                this.getProductionOrder(taskNumber, customId)
                            } else {
                                this.$store.dispatch('showNotification', {
                                    type: 'error',
                                    message: `Nie odnaleziono zlecenia produkcyjnego ${taskNumber}.`
                                })
                            }
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.findOrderLoading = false
                        })
                    break
                case 2:
                    this.getOrder(taskNumber)
                    break
                default:
                    break
            }
        },
        getProductionOrder(jobNumber, customId) {
            if (!jobNumber && !customId) return

            this.productionOrder = this.productionOrders.find(item => item.jobNumber == jobNumber)
            const id = customId || this.productionOrder.plates[0].custom_id

            if (!customId) {
                this.findOrderLoading = true
            } else {
                this.findPlateLoading = true
            }

            this.$store.dispatch('shipments/manageTracking/getProductionOrder', id)
                .then(response => {
                    this.isProductionOrder = true
                    this.task = response
                    this.isTaskFound = true

                    if (!customId) {
                        this.setDefaultPlate()
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.findOrderLoading = false
                    this.findPlateLoading = false
                })
        },
        getOrder(orderNumber) {
            this.findOrderLoading = true

            this.$store.dispatch('shipments/manageTracking/getOrder', orderNumber)
                .then(response => {
                    if (!isEmpty(response)) {
                        this.isProductionOrder = false
                        this.task = response
                        this.taskId = this.task.detailNrZamowienia
                        this.isTaskFound = true
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.findOrderLoading = false
                })
        },
        setDefaultPlate() {
            this.taskId = this.productionOrder.plates[0].custom_id
        },
        onConfirm() {
            this.$emit('on-confirm', {
                taskId: this.taskId,
                taskNumber: this.task.detailNrZamowienia || this.task.jobNumber,
                task: this.task
            })
        },
        showCodeReaderDialog() {
            this.$emit('code-stream-start', this.codeReaderSettings)
        },
        resetTaskSearch() {
            this.taskNumber = null
            this.taskId = null
            this.taskNumber = null
            this.task = {}
            this.isTaskFound = false
            this.productionOrders = []
            this.isProductionOrder = null
            this.$v.$reset()
        },
        onClose() {
            setTimeout(() => {
                this.taskTypeId = 1
                this.resetTaskSearch()
            }, 500)

            this.$emit('on-close')
        },
        // Walidacje
        taskTypeErrors() {
            const errors = []

            if (!this.$v.taskTypeId.$dirty) return errors
            !this.$v.taskTypeId.required && errors.push('Proszę wybrać rodzaj zadania.')

            return errors
        },
        taskNumberErrors() {
            const errors = []

            if (!this.$v.taskNumber.$dirty) return errors
            !this.$v.taskNumber.required && errors.push('Proszę podać numer zlecenia/zamówienia.')

            return errors
        },
    }
}
</script>
